/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import {
  Heading,
  Search,
  DateRange,
  WithBadge
} from '../'

import {
  hasDateChanged
} from '../../utils'

export class FilterSidebar extends React.Component {
  static propTypes = {
    heading: PropTypes.string,
    filters: PropTypes.arrayOf(PropTypes.any).isRequired,  // NOTE: can be array of 'element' if/when all filters get turned into components
    combine: PropTypes.arrayOf(PropTypes.number),  // array of filter numbers to combine into one filter item in sidebar
    filtersApplied: PropTypes.number,
    onClear: PropTypes.func,
    search: PropTypes.bool,
    searchString: PropTypes.string,
    dateRange: PropTypes.bool,
    dateRangeLabel: PropTypes.string,
    startDate: PropTypes.instanceOf(moment),
    endDate: PropTypes.instanceOf(moment),
    onSearchClick: PropTypes.func,
    onSearchClear: PropTypes.func,
    filterResetOrClearText: PropTypes.string,
    showTimeSelect: PropTypes.bool,
    componentFilters: PropTypes.bool,
    onApplyClick: PropTypes.func,
    className: PropTypes.string
  }

  static defaultProps = {
    searchString: ''
  }

  state = {
    searchString: this.props.searchString
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextProps.filtersApplied !== this.props.filtersApplied ||
      nextProps.filters.length !== this.props.filters.length) {
      return true
    }

    if (nextProps.dateRange) {
      if (nextProps.startDate === null && this.props.startDate === null) {
        return hasDateChanged(this.props.endDate, nextProps.endDate)
      }

      if (nextProps.startDate === null && this.props.startDate !== null) {
        return true
      } else if (nextProps.startDate.isSame(this.props.startDate)) {
        return hasDateChanged(this.props.endDate, nextProps.endDate)
      }
    }

    return true
  }

  render() {
    let dateFormat = this.props.showTimeSelect
      ? 'DD MMM YYYY, HH:mm'
      : 'DD MMM YYYY'

    return (
      <div className={`sidebar slds-col u-bg-color-white slds-size_2-of-12 slds-border_right ${this.props.className ? this.props.className : ''}`}>
        <div className="sidebar__header slds-p-around_large slds-m-top_medium">
          {this.props.actions &&
            <React.Fragment>
              <Heading
                size="small"
                className="u-font-weight-bold">
                ACTIONS
              </Heading>
              <div className='slds-p-top_medium slds-p-bottom_small'>
                {this.props.actions}
              </div>
            </React.Fragment>
          }
          {(this.props.search || this.props.dateRange || this.props.filters || this.props.componentFilters) &&
            <div className="slds-grid slds-m-top_large">
              <Heading
                size="small"
                className="u-font-weight-bold">
                {this.props.filtersApplied > 0
                  ? <WithBadge badgeContent={this.props.filtersApplied}>
                      FILTERS
                    </WithBadge>
                  : 'FILTERS'
                }
              </Heading>
              <div className="slds-col_bump-left slds-p-left_x-small">
                <div className="slds-grid slds-wrap">
                  {this.props.onApplyClick &&
                    <div className="slds-col slds-size_1-of-2 slds-text-align_right">
                      <a
                        className={`filter-sidebar_actions${this.props.filtersCanBeApplied ? '' : ' disabled'}`}
                        onClick={() => this.props.filtersCanBeApplied
                          ? this.props.onApplyClick()
                          : null
                        }
                        title='Apply filter changes'>
                        Apply
                      </a>
                    </div>
                  }
                  <div className="slds-col slds-size_1-of-2 slds-text-align_right">
                    {this.props.filtersApplied > 0
                      ? <a
                          className="filter-sidebar_actions"
                          onClick={this.props.onClear}
                          title='Clear all filters'>
                          {this.props.filterResetOrClearText || "Clear all"}
                        </a>
                      : null
                    }
                  </div>
                </div>
              </div>
            </div>
          }
          {this.props.search
            ? <Search
                placeholder="Search"
                value={this.state.searchString}
                onClick={() => this.props.onSearchClick(this.state.searchString)}
                onChange={(e) => this.setState({ searchString: e.target.value })}
                onClear={() => {
                  this.setState({ searchString: '' })
                  if (this.props.onSearchClear) {
                    this.props.onSearchClear()
                  }
                }}
                className="slds-m-top_medium slds-m-bottom_small"
              />
            : null
          }
          {this.props.dateRange &&
            <div className="slds-m-top_medium">
              {this.props.dateRangeLabel || "Date range:"}
              <div className="slds-m-left_small slds-m-top_xx-small">
                {this.props.showTimeSelect
                  ? <DateRange
                      startDate={this.props.startDate}
                      endDate={this.props.endDate}
                      onStartDateChange={this.props.onStartDateChange}
                      onEndDateChange={this.props.onEndDateChange}
                      startDatePlaceholder="From"
                      endDatePlaceholder="To"
                      maxDate={moment()}
                      dateFormat={dateFormat}
                      align="vertical"
                      showTimeSelect={true}
                      timeFormat="HH:mm"
                    />
                  : <DateRange
                      startDate={this.props.startDate}
                      endDate={this.props.endDate}
                      onStartDateChange={this.props.onStartDateChange}
                      onEndDateChange={this.props.onEndDateChange}
                      startDatePlaceholder="From"
                      endDatePlaceholder="To"
                      maxDate={moment()}
                      dateFormat={dateFormat}
                      align="vertical"
                    />
                }
              </div>
            </div>
          }
        </div>
        {this.props.componentFilters
          ? this.props.filters
          : this.props.filters.map((filter, index) => (
              this.props.combine
                ? this.props.combine.indexOf(index) > -1
                  ? Math.min(...this.props.combine) === index &&
                      <MultipleFilterSidebarItem
                        filters={this.props.filters.filter((item, itemIndex) => (
                          this.props.combine.indexOf(itemIndex) > -1
                        ))}
                        key={`combine_MultipleFilterSidebarItem_${filter.name}-${index}`}
                      />
                  : <FilterSidebarItem
                      title={filter.title}
                      options={filter.options}
                      key={`filterSidebarItem_${filter.title}`}
                    />
                : <FilterSidebarItem
                    title={filter.title}
                    options={filter.options}
                    key={`filterSidebarItem_${filter.title}`}
                  />
            ))
        }
      </div>
    )
  }
}

export const FilterSidebarItem = ({ title, options }) => (
  <div className="sidebar__item slds-p-top_medium slds-p-right_large slds-p-bottom_small slds-p-left_large">
    <div className="slds-grid slds-wrap">
      <div className="slds-col slds-size_1-of-1 sidebar__item__title">
        {title}
      </div>
      <div className="slds-col slds-size_3-of-4">
        <div className="sidebar__item__choice">
          {options}
        </div>
      </div>
    </div>
  </div>
)

FilterSidebarItem.propTypes = {
  title: PropTypes.string.isRequired,
  options: PropTypes.element.isRequired
}

const MultipleFilterSidebarItem = (props) => (
  <div className="sidebar__item slds-p-around_large">
    {props.filters.map((filter, index) => (
      <div className={`slds-grid slds-wrap${props.filters.length - 1 === index ? '' : " slds-m-bottom_medium"}`} key={`MultipleFilterSidebarItem_${filter.title}`}>
        <div className="slds-col slds-size_1-of-1 sidebar__item__title">
          {filter.title}
        </div>
        <div className="slds-col slds-size_3-of-4">
          <div className="sidebar__item__choice u-font-weight-bold">
            {filter.options}
          </div>
        </div>
      </div>
    ))}
  </div>
)

MultipleFilterSidebarItem.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      options: PropTypes.element.isRequired
    })
  )
}



// WEBPACK FOOTER //
// ./src/components/filter-sidebar/index.js